import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Avatar,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { Close, Search, Phone } from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import formatSerializedId from "../../utils/formatSerializedId";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark" || theme.palette.mode === "dark";
  return {
    panel: {
      position: "absolute",
      inset: 0,
      zIndex: 20,
      display: "flex",
      flexDirection: "column",
      background: dark ? "#0b1220" : "#ffffff",
      color: dark ? "#e2e8f0" : "#0f172a",
      minHeight: 0,
      borderTop: dark
        ? "1px solid rgba(148,163,184,0.12)"
        : "1px solid rgba(15,23,42,0.08)",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      padding: "10px 12px 8px",
      borderBottom: dark
        ? "1px solid rgba(148,163,184,0.14)"
        : "1px solid rgba(15,23,42,0.08)",
      flexShrink: 0,
    },
    title: {
      fontSize: 14,
      fontWeight: 700,
      color: dark ? "#f8fafc" : "#0f172a",
      margin: 0,
    },
    closeBtn: {
      color: dark ? "#94a3b8" : "#64748b",
      padding: 4,
      "&:hover": {
        color: dark ? "#e2e8f0" : "#0f172a",
        background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      },
    },
    body: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      padding: "10px 12px 12px",
      gap: 8,
    },
    searchRow: {
      position: "relative",
      flexShrink: 0,
    },
    searchSpinner: {
      position: "absolute",
      right: 12,
      top: "50%",
      transform: "translateY(-50%)",
      pointerEvents: "none",
      color: dark ? "#94a3b8" : "#64748b",
      zIndex: 1,
    },
    search: {
      // Spinner fica FORA do endAdornment — trocar adornment remonta o input e mata o caret
      "& .MuiOutlinedInput-root": {
        background: dark ? "#151e2e" : "#f8fafc",
        color: dark ? "#f8fafc" : "#0f172a",
        borderRadius: 10,
        fontSize: 13,
        paddingRight: 36,
        "& fieldset": {
          borderColor: dark
            ? "rgba(148,163,184,0.2)"
            : "rgba(15,23,42,0.12)",
        },
        "&:hover fieldset": {
          borderColor: dark
            ? "rgba(148,163,184,0.35)"
            : "rgba(15,23,42,0.22)",
        },
        "&.Mui-focused fieldset": {
          borderColor: dark ? "#3b82f6" : "#00339e",
        },
      },
      "& .MuiInputBase-input::placeholder": {
        color: dark ? "#64748b" : "#94a3b8",
        opacity: 1,
      },
      "& .MuiSvgIcon-root": {
        color: dark ? "#94a3b8" : "#64748b",
      },
    },
    list: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      padding: 0,
      scrollbarWidth: "thin",
      scrollbarColor: dark ? "#334155 transparent" : "#cbd5e1 transparent",
      "&::-webkit-scrollbar": { width: 6 },
      "&::-webkit-scrollbar-thumb": {
        background: dark ? "#334155" : "#cbd5e1",
        borderRadius: 4,
      },
    },
    item: {
      borderRadius: 10,
      marginBottom: 4,
      paddingTop: 6,
      paddingBottom: 6,
      "&:hover": {
        background: dark
          ? "rgba(59,130,246,0.14)"
          : "rgba(0,51,158,0.08)",
      },
    },
    primary: {
      color: dark ? "#f1f5f9" : "#0f172a",
      fontSize: 13,
      fontWeight: 600,
    },
    phone: {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: 12,
      color: dark ? "#93c5fd" : "#00339e",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
    },
    empty: {
      padding: "24px 8px",
      textAlign: "center",
      color: dark ? "#94a3b8" : "#64748b",
      fontSize: 12,
      lineHeight: 1.4,
      listStyle: "none",
    },
    avatar: {
      width: 36,
      height: 36,
      fontSize: 14,
      background: dark ? "#1e3a5f" : "#e8eef9",
      color: dark ? "#93c5fd" : "#00339e",
    },
  };
});

/**
 * Painel de contatos embutido no softphone (sobre o discador).
 * Busca via GET /contacts — mesmas regras de perfil + carteira do backend.
 */
const DialerContactPicker = ({ open, onClose, onSelect }) => {
  const classes = useStyles();
  const [searchParam, setSearchParam] = useState("");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const mountedRef = useRef(true);
  const inputRef = useRef(null);
  /** Enquanto o user digita na busca, não deixa re-render/socket tirar o caret. */
  const keepSearchFocusRef = useRef(false);
  const searchValueRef = useRef("");

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const restoreSearchFocus = useCallback(() => {
    if (!keepSearchFocusRef.current || !open) return;
    const el = inputRef.current;
    if (!el || el.disabled) return;
    if (document.activeElement === el) return;
    // Se o foco já está em outro campo legítimo (fora), não forçar
    const ae = document.activeElement;
    if (
      ae &&
      ae !== document.body &&
      ae !== document.documentElement &&
      ae !== el &&
      (ae.tagName === "INPUT" ||
        ae.tagName === "TEXTAREA" ||
        ae.isContentEditable) &&
      !el.contains?.(ae)
    ) {
      return;
    }
    try {
      el.focus({ preventScroll: true });
      // caret no fim (digitação)
      const len = el.value?.length ?? 0;
      el.setSelectionRange?.(len, len);
    } catch {
      /* ignore */
    }
  }, [open]);

  const fetchContacts = useCallback(async (term) => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts/", {
        params: {
          searchParam: term || "",
          pageNumber: 1,
        },
      });
      if (!mountedRef.current) return;
      const list = Array.isArray(data?.contacts) ? data.contacts : [];
      setContacts(
        list.filter(
          (c) =>
            c?.active !== false &&
            String(c?.number || "").replace(/\D/g, "").length >= 8
        )
      );
    } catch (err) {
      if (mountedRef.current) toastError(err);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        // Após setState/lista muda o caret some — devolve
        requestAnimationFrame(() => restoreSearchFocus());
      }
    }
  }, [restoreSearchFocus]);

  useEffect(() => {
    if (!open) {
      keepSearchFocusRef.current = false;
      return undefined;
    }
    setSearchParam("");
    searchValueRef.current = "";
    keepSearchFocusRef.current = true;
    fetchContacts("");
    const t = setTimeout(() => {
      keepSearchFocusRef.current = true;
      restoreSearchFocus();
    }, 80);
    return () => clearTimeout(t);
  }, [open, fetchContacts, restoreSearchFocus]);

  useEffect(() => {
    if (!open) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchContacts(searchParam.trim());
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchParam, open, fetchContacts]);

  // Reclaim se re-render do widget de voz (callState/ticker) tirou o caret do search
  useLayoutEffect(() => {
    if (!open || !keepSearchFocusRef.current) return;
    restoreSearchFocus();
  });

  if (!open) return null;

  return (
    <div
      className={classes.panel}
      role="dialog"
      aria-label="Contatos"
      onMouseDown={(e) => {
        // Clique no painel (não lista de opção): mantém intenção de digitar
        if (e.target.closest?.("button, a, [role='button'], .MuiListItem-button")) {
          keepSearchFocusRef.current = false;
        }
      }}
    >
      <div className={classes.header}>
        <p className={classes.title}>Contatos</p>
        <IconButton
          size="small"
          className={classes.closeBtn}
          onClick={onClose}
          aria-label="Fechar"
        >
          <Close fontSize="small" />
        </IconButton>
      </div>
      <div className={classes.body}>
        <div className={classes.searchRow}>
          <TextField
            className={classes.search}
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Buscar por nome ou número…"
            value={searchParam}
            onChange={(e) => {
              keepSearchFocusRef.current = true;
              searchValueRef.current = e.target.value;
              setSearchParam(e.target.value);
            }}
            onFocus={() => {
              keepSearchFocusRef.current = true;
            }}
            onBlur={() => {
              // Blur transitório de loading/list — devolve se ainda estamos em busca
              requestAnimationFrame(() => {
                if (!keepSearchFocusRef.current || !open) return;
                const ae = document.activeElement;
                if (
                  ae &&
                  ae !== document.body &&
                  ae.closest?.(".MuiListItem-button, button, a")
                ) {
                  keepSearchFocusRef.current = false;
                  return;
                }
                restoreSearchFocus();
              });
            }}
            inputRef={inputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              // NÃO usar endAdornment dinâmico (CircularProgress) — remonta o input
            }}
          />
          {loading ? (
            <CircularProgress size={16} className={classes.searchSpinner} />
          ) : null}
        </div>
        <List dense className={classes.list} disablePadding>
          {!loading && contacts.length === 0 ? (
            <li className={classes.empty}>
              {searchParam.trim()
                ? `Nenhum contato encontrado para “${searchParam.trim()}”.`
                : "Nenhum contato com número disponível (ou fora da sua carteira/perfil)."}
            </li>
          ) : (
            contacts.map((c) => {
              const number = String(c.number || "").replace(/\D/g, "");
              const display =
                typeof formatSerializedId === "function"
                  ? formatSerializedId(number) || number
                  : number;
              return (
                <ListItem
                  key={c.id}
                  button
                  className={classes.item}
                  onMouseDown={() => {
                    keepSearchFocusRef.current = false;
                  }}
                  onClick={() => {
                    keepSearchFocusRef.current = false;
                    onSelect?.({
                      id: c.id,
                      name: c.name || "",
                      phone: number,
                    });
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      className={classes.avatar}
                      src={c.profilePicUrl || undefined}
                    >
                      {(c.name || "?").charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <span className={classes.primary}>
                        {c.name || "Sem nome"}
                      </span>
                    }
                    secondary={
                      <span className={classes.phone}>
                        <Phone style={{ fontSize: 12 }} />
                        {display}
                      </span>
                    }
                  />
                </ListItem>
              );
            })
          )}
        </List>
      </div>
    </div>
  );
};

export default DialerContactPicker;
